import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import apiService from '../services/apiService';
import TelegramFeedbackFields from '../components/TelegramFeedbackFields';
import './UserCreate.css';
import './UserCreateAdmin.css';

const UserCreateAdmin = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { company, scrollY } = location.state || {};
    const backUrl = scrollY ? `/users?scrollY=${scrollY}` : '/users';
    const stores = (company?.units || []).flatMap(unit => unit.stores || []);

    const [fullname, setFullname] = useState('');
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [selectedStoreIds, setSelectedStoreIds] = useState([]);
    const [role, setRole] = useState('admin');
    const [submitting, setSubmitting] = useState(false);
    const [message, setMessage] = useState({ type: '', text: '' });
    const [passwordMismatch, setPasswordMismatch] = useState(false);
    const [whatsappPhone, setWhatsappPhone] = useState('');
    const [whatsappEnabled, setWhatsappEnabled] = useState(false);
    const [telegramUsername, setTelegramUsername] = useState('');
    const [feedbackTelegramEnabled, setFeedbackTelegramEnabled] = useState(false);
    const [feedbackTelegramShort, setFeedbackTelegramShort] = useState(true);
    const [feedbackTelegramLong, setFeedbackTelegramLong] = useState(false);

    const toggleStore = (storeId) => {
        setSelectedStoreIds((prev) =>
            prev.includes(storeId)
                ? prev.filter((id) => id !== storeId)
                : [...prev, storeId]
        );
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setMessage({ type: '', text: '' });

        if (!fullname.trim() || !username.trim() || !password || !confirmPassword) {
            setMessage({ type: 'error', text: 'Preencha todos os campos.' });
            return;
        }

        if (password !== confirmPassword) {
            setMessage({ type: 'error', text: 'As senhas não coincidem.' });
            return;
        }

        if (password.length < 4) {
            setMessage({ type: 'error', text: 'A senha deve ter pelo menos 4 caracteres.' });
            return;
        }

        if (selectedStoreIds.length === 0) {
            setMessage({ type: 'error', text: 'Selecione pelo menos uma loja.' });
            return;
        }

        try {
            setSubmitting(true);
            const result = await apiService.registerUser({
                fullname: fullname.trim(),
                username: username.trim(),
                password,
                company_id: company?.id,
                store_ids: selectedStoreIds.join(','),
                role,
                ...(whatsappPhone.trim() ? { whatsapp_phone: whatsappPhone.trim(),
                                            whatsapp_enabled: whatsappEnabled } : {}),
                ...(telegramUsername.trim() ? { telegram_username: telegramUsername.trim() } : {}),
                feedback_telegram_enabled: feedbackTelegramEnabled,
                ...(feedbackTelegramEnabled ? {
                    feedback_telegram_short: feedbackTelegramShort,
                    feedback_telegram_long: feedbackTelegramLong,
                } : {}),
            });

            if (result.success) {
                setMessage({ type: 'success', text: `Administrador "${fullname.trim()}" cadastrado com sucesso!` });
                setFullname('');
                setUsername('');
                setPassword('');
                setConfirmPassword('');
                setSelectedStoreIds([]);
                setRole('admin');
                setTelegramUsername('');
                setFeedbackTelegramEnabled(false);
                setFeedbackTelegramShort(true);
                setFeedbackTelegramLong(false);
            } else {
                const detail = JSON.stringify(result.details || '');
                if (detail.includes('duplicate key') || detail.includes('Username já existe')) {
                    setMessage({ type: 'error', text: 'Este nome de usuário já existe. Escolha outro.' });
                } else {
                    setMessage({ type: 'error', text: result.message || 'Erro ao cadastrar administrador.' });
                }
            }
        } catch (err) {
            setMessage({ type: 'error', text: 'Erro ao cadastrar administrador. Tente novamente.' });
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="user-create">
            <div className="user-create-header">
                <button className="btn-back" onClick={() => navigate(backUrl)}>
                    &#8592; Voltar
                </button>
                <h1>Cadastrar Administrador</h1>
            </div>

            {message.text && (
                <div className={`alert alert-${message.type}`}>
                    {message.text}
                </div>
            )}

            <form className="user-create-form" onSubmit={handleSubmit}>
                <div className="form-row">
                    <div className="form-group">
                        <label htmlFor="company">Empresa</label>
                        <input
                            id="company"
                            type="text"
                            value={company?.name || ''}
                            disabled
                            className="form-input"
                        />
                    </div>
                    <div className="form-group">
                        <label htmlFor="role">Perfil</label>
                        <select
                            id="role"
                            value={role}
                            onChange={(e) => setRole(e.target.value)}
                            className="form-input"
                        >
                            <option value="admin">Administrador</option>
                            <option value="director">Diretor</option>
                            <option value="supervisor">Supervisor</option>
                            <option value="manager">Gerente</option>
                        </select>
                    </div>
                </div>

                <div className="form-row">
                    <div className="form-group">
                        <label htmlFor="fullname">Nome Completo</label>
                        <input
                            id="fullname"
                            type="text"
                            value={fullname}
                            onChange={(e) => setFullname(e.target.value)}
                            className="form-input"
                            placeholder="Digite o nome completo"
                            autoFocus
                        />
                    </div>
                    <div className="form-group">
                        <label htmlFor="username">Usuário (login)</label>
                        <input
                            id="username"
                            type="text"
                            value={username}
                            onChange={(e) => setUsername(e.target.value)}
                            className="form-input"
                            placeholder="Digite o nome de usuário"
                        />
                    </div>
                </div>

                <div className="form-row">
                    <div className="form-group">
                        <label htmlFor="password">Senha</label>
                        <input
                            id="password"
                            type="password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            className="form-input"
                            placeholder="Digite a senha"
                        />
                    </div>
                    <div className="form-group">
                        <label htmlFor="confirmPassword">Confirmação da Senha</label>
                        <input
                            id="confirmPassword"
                            type="password"
                            value={confirmPassword}
                            onChange={(e) => {
                                setConfirmPassword(e.target.value);
                                setPasswordMismatch(false);
                            }}
                            onBlur={() => {
                                if (confirmPassword && confirmPassword !== password) {
                                    setPasswordMismatch(true);
                                }
                            }}
                            className={`form-input ${passwordMismatch ? 'form-input-error' : ''}`}
                            placeholder="Confirme a senha"
                        />
                        {passwordMismatch && (
                            <span className="field-error">As senhas não coincidem.</span>
                        )}
                    </div>
                </div>

                <div className="form-group stores-selection">
                    <label>Lojas com Acesso</label>
                    <div className="stores-checkboxes">
                        {stores.map((store) => (
                            <label key={store.id} className="store-checkbox-label">
                                <input
                                    type="checkbox"
                                    checked={selectedStoreIds.includes(store.id)}
                                    onChange={() => toggleStore(store.id)}
                                />
                                {store.name}
                            </label>
                        ))}
                    </div>
                </div>

                <div className="form-row">

                    <div className="form-group">

                        <label htmlFor="whatsappPhone">WhatsApp <span className="field-optional">(opcional)</span></label>

                        <input

                            id="whatsappPhone"

                            type="tel"

                            value={whatsappPhone}

                            onChange={(e) => setWhatsappPhone(e.target.value)}

                            className="form-input"

                            placeholder="(19) 98113-1421"

                        />

                        <small className="field-hint">

                            Com DDD. Pode digitar com parênteses e traço — o sistema arruma.

                        </small>

                    </div>
                    <div className="form-group">
                        <label htmlFor="whatsappEnabled">Receber relatórios por WhatsApp?</label>
                        <select
                            id="whatsappEnabled"
                            value={whatsappEnabled ? 'true' : 'false'}
                            onChange={(e) => setWhatsappEnabled(e.target.value === 'true')}
                            className="form-input"
                            disabled={!whatsappPhone.trim()}
                        >
                            <option value="false">Não</option>
                            <option value="true">Sim</option>
                        </select>
                        <small className="field-hint">
                            {whatsappPhone.trim()
                                ? 'Ao marcar Sim você registra o consentimento da pessoa. Desligar não apaga a data do primeiro sim.'
                                : 'Preencha o telefone para poder ligar o envio automático.'}
                        </small>
                    </div>

                </div>


                <TelegramFeedbackFields
                    role={role}
                    username={telegramUsername}
                    onUsernameChange={setTelegramUsername}
                    enabled={feedbackTelegramEnabled}
                    onEnabledChange={setFeedbackTelegramEnabled}
                    short={feedbackTelegramShort}
                    onShortChange={setFeedbackTelegramShort}
                    long={feedbackTelegramLong}
                    onLongChange={setFeedbackTelegramLong}
                    mostrarVinculo={false}
                />
                <div className="form-actions">
                    <button
                        type="button"
                        className="btn btn-cancel"
                        onClick={() => navigate(backUrl)}
                    >
                        Cancelar
                    </button>
                    <button
                        type="submit"
                        className="btn btn-submit"
                        disabled={submitting}
                    >
                        {submitting ? 'Cadastrando...' : 'Cadastrar Administrador'}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default UserCreateAdmin;